import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { createOrUpdateCustomerProfile } from '../api/customerAuthService';
import '../styles/customerAuth.css';

function CustomerProfileCompletion() {
  const { currentUser, salonId } = useCustomerAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    phoneNumber: currentUser?.phoneNumber || '',
    preferences: {
      notifications: {
        email: true,
        push: false
      }
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prevState => ({
        ...prevState,
        [parent]: {
          ...prevState[parent],
          [child]: {
            ...prevState[parent][child],
            [e.target.dataset.key]: checked
          }
        }
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: checked
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.displayName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setLoading(true);
    
    try {
      // If phoneNumber is empty, set to null to avoid validation issues
      const phoneNumber = formData.phoneNumber.trim() ? formData.phoneNumber : null;
      
      // Update user profile with form data
      await createOrUpdateCustomerProfile({
        ...currentUser,
        displayName: formData.displayName,
        phoneNumber,
        email: currentUser.email
      }, salonId);
      
      // Redirect to customer dashboard
      navigate(`/BespokeBeauty/${salonId}/customer/home`);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // If no user is logged in, redirect to login
  if (!currentUser) {
    navigate(`/salon/${salonId}/login`);
    return null;
  }
  
  return (
    <div className="customer-auth-container">
      <div className="customer-auth-card">
        <h2>Complete Your Profile</h2>
        
        {error && <div className="customer-auth-error">{error}</div>}
        
        <p>Please provide a few more details to complete your profile.</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="displayName">Your Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Your full name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number (optional)</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              onChange={handleChange}
              placeholder="Your phone number"
            />
            <p className="field-help">For appointment reminders and notifications.</p>
          </div>
          
          <div className="form-group">
            <label className="checkbox-label">Notification Preferences</label>
            
            <div className="checkbox-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="preferences.notifications"
                  data-key="email"
                  checked={formData.preferences.notifications.email}
                  onChange={handleCheckboxChange}
                />
                <span>Email notifications</span>
              </label>
              
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="preferences.notifications"
                  data-key="push"
                  checked={formData.preferences.notifications.push}
                  onChange={handleCheckboxChange}
                />
                <span>Push notifications</span>
              </label>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="customer-auth-button"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Complete Profile'}
          </button>
        </form>
        
        <div className="skip-section">
          <button 
            className="text-button"
            onClick={() => navigate(`/BespokeBeauty/${salonId}/customer/home`)}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomerProfileCompletion;